import React, { useState, useEffect } from "react";
import { Card, PageHeader, Button, Calendar, TimePicker, DateRangePicker, StatusBadge } from "../../../components/common";
import {
  useAppDispatch,
  useAppSelector,
  getStudentsThunk,
  getAttendanceThunk,
  createAttendanceThunk,
} from "../../../redux";
import { showSuccess } from "../../../services";
import { AttendanceStatus, AttendanceFieldRecord } from "../../../types";
import { formatTimeToDisplay, updateIsoDate, parseYMD, formatYMD } from "../../../utils";
import calendarIcon from "../../../assets/icons/calendar.svg";
import clockIcon from "../../../assets/icons/clock.svg";
import "./TeacherAttendance.css";

const TeacherAttendance: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { records } = useAppSelector((state) => state.student);
  const { records: attendanceHistory } = useAppSelector((state) => state.attendance);

  // Tab State
  const [activeTab, setActiveTab] = useState<"student" | "search">("student");

  // Picker Popover State
  const [openPicker, setOpenPicker] = useState<
    "attendanceDate" | "searchDateRange" | "fromTime" | "toTime" | null
  >(null);

  // Marking State
  const todayStr = formatYMD(new Date());
  const [attendanceDate, setAttendanceDate] = useState<string>(todayStr);
  const [fromTime, setFromTime] = useState<string>(`${todayStr}T09:00:00+10:00`);
  const [toTime, setToTime] = useState<string>(`${todayStr}T10:30:00+10:00`);
  const [remarks, setRemarks] = useState<string>("");
  const [statusMap, setStatusMap] = useState<Record<string, AttendanceStatus>>({});

  // Search Attendance State
  const [searchStartDate, setSearchStartDate] = useState<string>(todayStr);
  const [searchEndDate, setSearchEndDate] = useState<string>(todayStr);

  const handleSearchAttendance = (start = searchStartDate, end = searchEndDate, force = true) => {
    if (!user?.id) return;

    const selectQuery = `select id, Name, Teacher, Student, Date, Attendance_Status, From_Time, To_Time from Attendance where Teacher = '${user.id}' and Date between '${start}' and '${end}' order by Date desc limit 200`;

    dispatch(getAttendanceThunk({ query: selectQuery, force }));
  };

  useEffect(() => {
    if (user?.id) {
      dispatch(getStudentsThunk(user.id));
      handleSearchAttendance(todayStr, todayStr, true);
    }
  }, [dispatch, user?.id]);

  useEffect(() => {
    const initialStatus: Record<string, AttendanceStatus> = {};
    records.forEach((student) => {
      initialStatus[student.id] = "Present";
    });
    setStatusMap(initialStatus);
  }, [records]);

  // Outside click listener for calendar and time popovers
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".date-picker-wrapper") && !target.closest(".time-picker-wrapper")) {
        setOpenPicker(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleSelectDate = (
    key: "attendanceDate",
    value: Date
  ) => {
    const formatted = formatYMD(value);
    if (key === "attendanceDate") {
      setAttendanceDate(formatted);
      setFromTime((prev) => updateIsoDate(prev, formatted));
      setToTime((prev) => updateIsoDate(prev, formatted));
    }
    setOpenPicker(null);
  };

  const handleBulkMark = (status: AttendanceStatus) => {
    const updatedStatus: Record<string, AttendanceStatus> = {};
    records.forEach((student) => {
      updatedStatus[student.id] = status;
    });
    setStatusMap(updatedStatus);
  };

  const handleStatusChange = (studentId: string, status: AttendanceStatus) => {
    setStatusMap((prev) => ({
      ...prev,
      [studentId]: status,
    }));
  };

  const handleSubmit = () => {
    if (!user?.id || records.length === 0) return;

    const fields: AttendanceFieldRecord[] = records.map((student) => ({
      Name: `${[student.First_Name, student.Middle_Name, student.Last_Name].filter(Boolean).join(" ")} - ${attendanceDate} - 9:00 AM`,
      Teacher: user.id,
      Student: student.id,
      Date: attendanceDate,
      Attendance_Status: statusMap[student.id],
      From_Time: fromTime,
      To_Time: toTime,
      Remarks: remarks,
    }));

    dispatch(
      createAttendanceThunk({
        fields,
        trigger: ["workflow"],
      })
    ).then((res) => {
      if (createAttendanceThunk.fulfilled.match(res)) {
        showSuccess("Attendance saved successfully!");
        handleSearchAttendance();
      }
    });
  };

  // Extract unique session columns from attendanceHistory
  const sessionColumns: { key: string; date: string; time: string }[] = [];
  const sessionKeys = new Set<string>();

  attendanceHistory.forEach((item) => {
    const key = `${item.Date}_${item.From_Time}_${item.To_Time}`;
    if (!sessionKeys.has(key)) {
      sessionKeys.add(key);
      sessionColumns.push({
        key,
        date: item.Date,
        time: `${formatTimeToDisplay(item.From_Time)} - ${formatTimeToDisplay(item.To_Time)}`,
      });
    }
  });

  // Map status by student ID and session key
  const historyStatusMap = new Map<string, AttendanceStatus>();
  attendanceHistory.forEach((item) => {
    if (item.Student?.id) {
      historyStatusMap.set(`${item.Student.id}_${item.Date}_${item.From_Time}_${item.To_Time}`, item.Attendance_Status);
    }
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card className="attendance-page-card attendance-header-card" style={{ padding: "24px 24px 0 24px" }}>
        <PageHeader
          title="Student Attendance"
          subtitle="Mark and search attendance for your allocated students."
        />
        <div className="attendance-tabs">
          <button
            type="button"
            className={`tab-btn ${activeTab === "student" ? "active" : ""}`}
            onClick={() => setActiveTab("student")}
          >
            Student Attendance
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === "search" ? "active" : ""}`}
            onClick={() => setActiveTab("search")}
          >
            Search Attendance
          </button>
        </div>
      </Card>

      {activeTab === "student" && (
        <>
          <Card className="attendance-page-card" style={{ padding: "24px" }}>
            <div className="attendance-list-header">
              <h3 style={{ margin: 0 }}>Student Attendance</h3>
              <div className="bulk-buttons">
                <Button variant="secondary" size="small" onClick={() => handleBulkMark("Present")}>
                  Mark All Present
                </Button>
                <Button variant="outline" size="small" onClick={() => handleBulkMark("Absent")}>
                  Mark All Absent
                </Button>
              </div>
            </div>

            <div className="student-attendance-table">
              {records.map((student) => {
                const currentStatus = statusMap[student.id];

                return (
                  <div key={student.id} className="student-attendance-row">
                    <div className="student-info">
                      <span className="student-name">
                        {student.Student_Number2} - {[student.First_Name, student.Middle_Name, student.Last_Name].filter(Boolean).join(" ")}
                      </span>
                    </div>

                    <div className="attendance-actions">
                      <button
                        type="button"
                        className={`status-btn present ${currentStatus === "Present" ? "active" : ""}`}
                        onClick={() => handleStatusChange(student.id, "Present")}
                      >
                        Present
                      </button>
                      <button
                        type="button"
                        className={`status-btn absent ${currentStatus === "Absent" ? "active" : ""}`}
                        onClick={() => handleStatusChange(student.id, "Absent")}
                      >
                        Absent
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="attendance-page-card" style={{ padding: "24px" }}>
            <h3 style={{ margin: "0 0 16px 0" }}>Attendance Details</h3>
            <div className="attendance-form-grid">
              <div className="attendance-field date-picker-wrapper">
                <label>Date</label>
                <div
                  className="input-with-icon"
                  onClick={() =>
                    setOpenPicker((prev) => (prev === "attendanceDate" ? null : "attendanceDate"))
                  }
                >
                  <input type="text" readOnly value={attendanceDate} className="date-input" />
                  <img src={calendarIcon} alt="Calendar" className="calendar-icon-img" />
                </div>
                {openPicker === "attendanceDate" && (
                  <div className="calendar-popover-dropdown">
                    <Calendar
                      value={parseYMD(attendanceDate)}
                      onChange={(val) => handleSelectDate("attendanceDate", val as Date)}
                    />
                  </div>
                )}
              </div>

              <div className="attendance-field time-picker-wrapper">
                <label>From Time</label>
                <div
                  className="input-with-icon"
                  onClick={() =>
                    setOpenPicker((prev) => (prev === "fromTime" ? null : "fromTime"))
                  }
                >
                  <input
                    type="text"
                    readOnly
                    value={formatTimeToDisplay(fromTime)}
                    className="time-input"
                  />
                  <img src={clockIcon} alt="Clock" className="clock-icon-img" />
                </div>
                {openPicker === "fromTime" && (
                  <div className="time-popover-dropdown">
                    <TimePicker
                      value={fromTime}
                      onChange={(newIso) => {
                        setFromTime(newIso);
                        setOpenPicker(null);
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="attendance-field time-picker-wrapper">
                <label>To Time</label>
                <div
                  className="input-with-icon"
                  onClick={() =>
                    setOpenPicker((prev) => (prev === "toTime" ? null : "toTime"))
                  }
                >
                  <input
                    type="text"
                    readOnly
                    value={formatTimeToDisplay(toTime)}
                    className="time-input"
                  />
                  <img src={clockIcon} alt="Clock" className="clock-icon-img" />
                </div>
                {openPicker === "toTime" && (
                  <div className="time-popover-dropdown">
                    <TimePicker
                      value={toTime}
                      onChange={(newIso) => {
                        setToTime(newIso);
                        setOpenPicker(null);
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="attendance-field">
                <label>Remarks</label>
                <input
                  type="text"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            </div>

            <div className="submit-container">
              <Button
                variant="primary"
                size="large"
                onClick={handleSubmit}
                disabled={records.length === 0}
              >
                Save Attendance
              </Button>
            </div>
          </Card>
        </>
      )}

      {activeTab === "search" && (
        <Card className="attendance-page-card" style={{ padding: "24px" }}>
          <h3 style={{ margin: "0 0 16px 0" }}>Search Attendance</h3>
          <div className="attendance-search-bar">
            <DateRangePicker
              label="Date Range"
              startDate={searchStartDate}
              endDate={searchEndDate}
              isOpen={openPicker === "searchDateRange"}
              onToggle={() =>
                setOpenPicker((prev) => (prev === "searchDateRange" ? null : "searchDateRange"))
              }
              onChange={(start, end) => {
                setSearchStartDate(start);
                setSearchEndDate(end);
                setOpenPicker(null);
              }}
            />

            <div className="attendance-field search-btn-container">
              <Button
                variant="secondary"
                size="medium"
                onClick={() => handleSearchAttendance()}
              >
                Search Attendance
              </Button>
            </div>
          </div>

          {attendanceHistory.length === 0 ? (
            <div className="no-history-records">
              No attendance records found for the selected date range.
            </div>
          ) : (
            <div className="excel-matrix-container">
              <div className="excel-matrix-left">
                <div className="student-header-cell">
                  <span className="header-student-title">Student Name</span>
                </div>
                {records.map((student) => (
                  <div key={student.id} className="student-body-cell">
                    <span className="student-cell-name">
                      {student.Student_Number2} - {[student.First_Name, student.Middle_Name, student.Last_Name].filter(Boolean).join(" ")}
                    </span>
                  </div>
                ))}
              </div>

              <div className="excel-matrix-right">
                <table
                  className="excel-matrix-table"
                  style={{
                    width: sessionColumns.length > 6 ? `${(sessionColumns.length / 6) * 100}%` : "100%",
                    minWidth: `${sessionColumns.length * 130}px`,
                  }}
                >
                  <thead>
                    <tr>
                      {sessionColumns.map((col) => (
                        <th key={`date-${col.key}`} className="date-header">
                          {col.date}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {sessionColumns.map((col) => (
                        <th key={`time-${col.key}`} className="time-header">
                          {col.time}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((student) => (
                      <tr key={student.id} className="matrix-row">
                        {sessionColumns.map((col) => {
                          const status = historyStatusMap.get(`${student.id}_${col.key}`);
                          return (
                            <td key={`${student.id}-${col.key}`} className="status-cell">
                              {status ? (
                                <StatusBadge variant={status === "Present" ? "success" : "error"}>
                                  {status}
                                </StatusBadge>
                              ) : (
                                <span className="no-status">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default TeacherAttendance;
